import { BookmarkBorder, Close } from '@mui/icons-material';
import { Badge, Input } from '@mui/joy';
import { ArrowLeft, ArrowRight, MagnifyingGlass } from '@phosphor-icons/react';
import { GridApi } from 'ag-grid-community';
import { Dispatch, ReactElement, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionButton, Button } from 'core/components';
import { MenuButton } from 'core/elements';
import { TableLayoutType } from 'core/enums';
import {
  AgGridDataRow,
  IOption,
  ISelectionAction,
  ISelectionActionProps,
  ITableAction,
  ITableActionProps,
} from 'core/types';
import { enterPress } from 'core/utils';
import { Color, Level, Size, Variant } from 'theme/enums';

import { GroupByButton } from './GroupByButton';
import { ManageFiltersButton } from './ManageFilters';
import { CloseSelectionButton, LeftWrapper, RightWrapper, SelectionLabel, Wrapper } from './style';

interface IProps {
  tableId: string;
  hasQuickSearch: boolean;
  isSearchActive: boolean;
  onSearchIconClick: () => void;
  searchText: string;
  setSearchText: Dispatch<SetStateAction<string>>;
  onQuickFilterSubmit?: () => void;
  onQuickFilterReset?: () => void;
  scroll?: (isLeftScroll?: boolean) => void;
  isScrollLeftDisabled?: boolean;
  isScrollRightDisabled?: boolean;
  isDataLoading?: boolean;
  gridApi?: GridApi;
  selectedRows?: AgGridDataRow[];
  deselectAllRows?: () => void;
  handleOnSelectionButtonClick?: (action: ISelectionAction) => void;
  onCloseSelection?: () => void;
  actions?: () => ITableAction[];
  actionButtons?: ReactElement[] | ((props: ITableActionProps) => ReactElement[]);
  rightActionButtons?: ReactElement[];
  selectionActions?: (props: ISelectionActionProps) => ISelectionAction[];
  selectionActionButtons?: ReactElement[] | ((props: ISelectionActionProps) => ReactElement[]);
  selectionType?: string;
  isTableLoadingCompleted?: boolean;
  saveLayoutDisabled?: boolean;
  onSaveLayout?: () => void;
  layoutType: TableLayoutType;
  hasLayoutActions: boolean;
  hasManageFilters?: boolean;
  resetMenuItems?: IOption[];
  hasGroupByEnabled?: boolean;
}

const TopActionsView = ({
  tableId,
  hasQuickSearch,
  isSearchActive,
  onSearchIconClick,
  searchText,
  setSearchText,
  onQuickFilterSubmit,
  onQuickFilterReset,
  scroll,
  isScrollLeftDisabled,
  isScrollRightDisabled,
  isDataLoading,
  gridApi,
  selectedRows,
  deselectAllRows,
  handleOnSelectionButtonClick,
  onCloseSelection,
  actions,
  actionButtons = [],
  rightActionButtons = [],
  selectionActions,
  selectionActionButtons = [],
  selectionType,
  isTableLoadingCompleted,
  saveLayoutDisabled,
  onSaveLayout,
  layoutType,
  hasLayoutActions,
  hasManageFilters,
  resetMenuItems,
  hasGroupByEnabled,
}: IProps) => {
  const { t } = useTranslation();

  const showResetButton = resetMenuItems && resetMenuItems.filter((item) => !item?.hide).length > 0;

  const QuickSearch = !hasQuickSearch ? null : isSearchActive ? (
    <Input
      name="quickSearch"
      placeholder={t('labelQuickSearch')}
      value={searchText}
      onChange={(event) => setSearchText(event.target.value)}
      onKeyDown={(event) => enterPress(event, onQuickFilterSubmit)}
      endDecorator={<Button color={Color.PRIMARY} icon={<Close />} onClick={onQuickFilterReset} />}
      style={{ width: 300 }}
      autoFocus
    />
  ) : (
    !isSearchActive && (
      <Button
        color={Color.PRIMARY}
        variant={Variant.SOFT}
        icon={<MagnifyingGlass />}
        onClick={onSearchIconClick}
        disabled={!isTableLoadingCompleted}
      />
    )
  );

  const actionButtonsArray = Array.isArray(actionButtons)
    ? actionButtons
    : actionButtons({ gridApi });

  const selectionActionButtonsArray = Array.isArray(selectionActionButtons)
    ? selectionActionButtons
    : selectionActionButtons({ gridApi, selectedRows, deselectAllRows });

  if (selectedRows?.length) {
    return (
      <Wrapper>
        <LeftWrapper>
          <CloseSelectionButton
            icon={<Close />}
            variant={Variant.OUTLINED}
            color={Color.PRIMARY}
            onClick={onCloseSelection}
          />
          <SelectionLabel level={Level.TITLE_MD}>
            {t('labelSelectionCount', {
              count: selectedRows?.length,
              type: selectionType
                .replace(/([A-Z])/g, ' $1')
                .toLowerCase()
                .replace(/([^aeiou])y$/, selectedRows?.length > 1 ? '$1ie' : '$1y'),
            })}
          </SelectionLabel>
          {selectionActions &&
            selectionActions({ selectedRows, gridApi, deselectAllRows }).map(
              (action: ISelectionAction, index: number) => {
                if (action?.hide) {
                  return;
                }

                if (action?.options) {
                  return (
                    <MenuButton
                      key={`${action?.label}-${index}`}
                      label={action?.label}
                      menuItems={action?.options}
                      color={action?.color || Color.NEUTRAL}
                      variant={action?.variant || Variant.OUTLINED}
                      disabled={action?.disabled}
                    />
                  );
                }

                return (
                  <Button
                    key={`${action?.label}-${index}`}
                    text={action?.label}
                    startDecorator={action?.startDecorator}
                    endDecorator={action?.endDecorator}
                    icon={action?.icon}
                    color={action?.color || Color.NEUTRAL}
                    variant={action?.variant || Variant.OUTLINED}
                    onClick={() => handleOnSelectionButtonClick(action)}
                    disabled={action?.disabled}
                  />
                );
              }
            )}

          {selectionActionButtonsArray.length > 0 && selectionActionButtonsArray}
        </LeftWrapper>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <LeftWrapper>
        {QuickSearch}

        {actions &&
          actions().map(
            (action: ITableAction, index: number) =>
              !action?.hide && <ActionButton key={`${action?.label}-${index}`} action={action} />
          )}

        {actionButtonsArray.length > 0 && actionButtonsArray}
      </LeftWrapper>

      <RightWrapper>
        {rightActionButtons.length > 0 && rightActionButtons}

        {showResetButton && (
          <Badge size={Size.SM} invisible={resetMenuItems.every((item) => !item.badge)}>
            <MenuButton
              label={t('buttonLabelReset')}
              menuItems={resetMenuItems}
              color={Color.NEUTRAL}
              variant={Variant.OUTLINED}
            />
          </Badge>
        )}

        {hasGroupByEnabled && <GroupByButton />}

        {hasLayoutActions && (
          <Button
            text={t('buttonLabelSaveLayout')}
            color={Color.NEUTRAL}
            variant={Variant.OUTLINED}
            onClick={onSaveLayout}
            startDecorator={<BookmarkBorder />}
            disabled={!isTableLoadingCompleted || saveLayoutDisabled}
          />
        )}

        {hasManageFilters && <ManageFiltersButton entity={layoutType} gridApi={gridApi} />}

        <Button
          key={`scroll-left-${tableId}`}
          variant={Variant.SOFT}
          icon={<ArrowLeft />}
          onClick={() => scroll(true)}
          disabled={isDataLoading || isScrollLeftDisabled}
        />

        <Button
          key={`scroll-right-${tableId}`}
          variant={Variant.SOFT}
          icon={<ArrowRight />}
          onClick={() => scroll()}
          disabled={isDataLoading || isScrollRightDisabled}
        />
      </RightWrapper>
    </Wrapper>
  );
};

export default TopActionsView;
